declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

const isProduction = process.env.REACT_APP_ENV === "production";

export const EVENT_TYPE = {
  OPEN: "app_open",
  LOGIN: "login",
  PLAY_VIDEO: "video_play",
  WORKOUT_FAVORITE: "workout_favorite",
  WORKOUT_DETAILS: "workout_details",
  WORKOUT_CATEGORY: "workout_category",
  WORKOUT_CATEGORY_BY_DAY: "workout_category_by_day",
  WORKOUT_MOVEMENT: "workout_movement",
  SEARCH_FILTERS: "search_filters",
} as const;

export const SCREEN_NAMES = {
  SIGN_IN: "Sign In",
  FORGOT_PASSWORD: "Forgot Password",
  WORKOUT: "Workout",
  WORKOUT_CATEGORY_DETAILS: "Workout Category Details",
  CALENDAR: "Calendar",
  WORKOUT_FAVORITES: "Favorites",
  WORKOUT_CATEGORY_BY_DAY: "Workout Category By Day",
  WORKOUT_DETAILS: "Workout Details",
  MOVEMENT_DETAILS: "Movement Details",
  WORKOUT_FAMILY: "Workout Family",
} as const;

export type EventTypes = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];
type EventData = {
  id?: any;
  title?: string;
  date?: string;
  value?: string | number;
  barcode?: string;
  source?: string;
};
type Event = {
  event: EventTypes;
  data?: EventData;
};

type LogScreenViewParams = {
  pathname: string;
  screenName: string;
  data?: {
    id?: string;
    title?: string;
    date?: string;
  };
};

const getAnalyticsEventOptions = (data?: EventData) => {
  const options = {
    ...data,
    ...(!isProduction ? { debug_mode: true } : {}),
  };

  return options;
};

export const logEvent = ({ event, data }: Event) => {
  if (typeof window.gtag !== "function") {
    console.warn("[Analytics] gtag is not available");
    return;
  }

  const eventOptions = getAnalyticsEventOptions(data);

  if (!isProduction) {
    console.log(`[Analytics] 🎯 Sending Event: "${event}"`, eventOptions);
    console.log(
      `[Analytics] Debug mode enabled: ${eventOptions.debug_mode === true}`,
    );
  }

  window.gtag("event", event, eventOptions);
};

export const logScreenView = ({
  screenName,
  pathname,
  data,
}: LogScreenViewParams) => {
  if (typeof window.gtag !== "function") {
    console.warn("[Analytics] gtag is not available for screen view");
    return;
  }

  const eventOptions = {
    page_title: screenName,
    page_path: pathname,
    screen_name: screenName,
    ...getAnalyticsEventOptions(data),
  };

  if (!isProduction) {
    console.log(
      `[Analytics] 📱 Sending Screen View: "${screenName}"`,
      eventOptions,
    );
  }

  window.gtag("event", "page_view", eventOptions);
};

export const setAnalyticsUserId = (id: string | null) => {
  if (!isProduction) console.debug("[Analytics] User ID:", id);

  const trackingId = process.env.REACT_APP_GA_ID;
  if (!trackingId) {
    console.warn("GA ID not found for user config");
    return;
  }

  window.gtag?.("config", trackingId, {
    user_id: id ?? null,
  });
};

export const setUserProperties = (params: { [key: string]: string }) => {
  if (!isProduction) console.debug("[Analytics] User properties:", params);

  window.gtag?.("set", "user_properties", params);
};
